import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fcfa } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarCheck, FileText, Receipt, Clock } from "lucide-react";

const STATUS_COLOR = { confirme: "bg-accent text-accent-foreground", annule: "bg-destructive text-white", en_attente: "bg-amber-500 text-white", devis_recu: "bg-secondary text-white", accepte: "bg-accent text-accent-foreground" };

export default function ClientDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);

  const load = () => {
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/quote-requests").then((r) => setRequests(r.data));
    api.get("/payments").then((r) => setPayments(r.data));
  };
  useEffect(() => { load(); }, []);

  const accept = async (qid) => {
    try { await api.post(`/quotes/${qid}/accept`); toast.success("Devis accepté !"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary">Bonjour, {user.name?.split(" ")[0]} 👋</h1>
      <p className="text-muted-foreground">Gérez vos RDV, devis et paiements.</p>

      <div className="grid grid-cols-3 gap-3 mt-6">
        {[{ icon: CalendarCheck, label: "RDV", value: appts.length }, { icon: FileText, label: "Devis", value: requests.length }, { icon: Receipt, label: "Paiements", value: payments.filter((p) => p.status === "paid").length }].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-4 sm:p-6">
            <s.icon className="w-6 h-6 text-primary mb-2" />
            <div className="font-display text-2xl font-bold text-secondary">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="rdv" className="mt-8">
        <TabsList>
          <TabsTrigger value="rdv" data-testid="tab-appointments">Mes RDV</TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-quotes">Mes devis</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-payments">Paiements</TabsTrigger>
        </TabsList>

        <TabsContent value="rdv" className="space-y-3 mt-4">
          {appts.length === 0 && <Empty text="Aucun RDV. Trouvez un prestataire." link />}
          {appts.map((a) => (
            <div key={a.appt_id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="font-medium text-secondary">{a.service || "Prestation"}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{a.date} à {a.slot}</div>
              </div>
              <div className="flex items-center gap-2">
                {a.paid && <Badge className="bg-accent text-accent-foreground">Payé</Badge>}
                <Badge className={STATUS_COLOR[a.status] || ""}>{a.status}</Badge>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="devis" className="space-y-3 mt-4">
          {requests.length === 0 && <Empty text="Aucune demande de devis." link />}
          {requests.map((r) => (
            <div key={r.request_id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium text-secondary">{r.service}</div>
                <Badge className={STATUS_COLOR[r.status] || ""}>{r.status}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
              {r.quotes?.map((q) => (
                <div key={q.quote_id} className="mt-3 p-3 rounded-md bg-muted flex items-center justify-between">
                  <div>
                    <div className="font-display font-bold text-primary">{fcfa(q.amount)}</div>
                    <div className="text-xs text-muted-foreground">{q.message}</div>
                  </div>
                  {q.status === "accepte" ? <Badge className="bg-accent text-accent-foreground">Accepté</Badge>
                    : <Button size="sm" data-testid={`accept-${q.quote_id}`} className="bg-primary hover:bg-primary/90" onClick={() => accept(q.quote_id)}>Accepter</Button>}
                </div>
              ))}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="paiements" className="space-y-3 mt-4">
          {payments.length === 0 && <Empty text="Aucun paiement." />}
          {payments.map((p) => (
            <div key={p.tx_id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="font-medium text-secondary capitalize">{p.kind}</div>
                <div className="text-xs text-muted-foreground">{p.method} · {new Date(p.created_at).toLocaleDateString("fr-FR")}</div>
              </div>
              <div className="text-right">
                <div className="font-display font-bold text-secondary">{fcfa(p.amount)}</div>
                <Badge className={p.status === "paid" ? "bg-accent text-accent-foreground" : "bg-amber-500 text-white"}>{p.status === "paid" ? "Payé" : "En attente"}</Badge>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ text, link }) {
  return (
    <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
      <p>{text}</p>
      {link && <Link to="/prestataires"><Button variant="outline" size="sm" className="mt-3">Voir les prestataires</Button></Link>}
    </div>
  );
}
